import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { useTranslation } from '../../context/LanguageContext';
import './CompanyRegister.css';

function CompanyRegister() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    ceo: '',
    businessNumber: '',
    address: '',
    phone: '',
    employees: '',
    status: 'active'
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('pages.admin.companyRegister.error.nameRequired');
    }
    if (!formData.code.trim()) {
      newErrors.code = t('pages.admin.companyRegister.error.codeRequired');
    }
    if (!formData.ceo.trim()) {
      newErrors.ceo = t('pages.admin.companyRegister.error.ceoRequired');
    }
    if (!formData.businessNumber.trim()) {
      newErrors.businessNumber = t('pages.admin.companyRegister.error.businessNumberRequired');
    } else if (!/^\d{3}-\d{2}-\d{5}$/.test(formData.businessNumber)) {
      newErrors.businessNumber = t('pages.admin.companyRegister.error.businessNumberInvalid');
    }
    if (!formData.address.trim()) {
      newErrors.address = t('pages.admin.companyRegister.error.addressRequired');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t('pages.admin.companyRegister.error.phoneRequired');
    } else if (!/^\d{2,3}-\d{3,4}-\d{4}$/.test(formData.phone)) {
      newErrors.phone = t('pages.admin.companyRegister.error.phoneInvalid');
    }
    if (!formData.employees) {
      newErrors.employees = t('pages.admin.companyRegister.error.employeesRequired');
    } else if (isNaN(formData.employees) || formData.employees < 1) {
      newErrors.employees = t('pages.admin.companyRegister.error.employeesInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // TODO: API 호출로 회사 등록

    navigate('/admin');
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  return (
    <MainLayout>
      <div className="company-register-container">
        <div className="company-register-header">
          <div className="company-register-header-left">
            <div className="back-btn" onClick={handleCancel}>
              <span class="material-symbols-outlined">
                arrow_back_ios
              </span>
            </div>
            <div>
              <div className="company-register-title">{t('pages.admin.companyRegister.title')}</div>
              <p className="company-register-subtitle">{t('pages.admin.companyRegister.subtitle')}</p>
            </div>
          </div>
        </div>

        <form className="company-register-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h3 className="section-title">{t('pages.admin.companyRegister.sectionBasic')}</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  {t('pages.admin.companyRegister.fields.name')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('pages.admin.companyRegister.placeholder.name')}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('pages.admin.companyRegister.fields.code')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="code"
                  className={`form-input ${errors.code ? 'error' : ''}`}
                  value={formData.code}
                  onChange={handleChange}
                  placeholder={t('pages.admin.companyRegister.placeholder.code')}
                />
                {errors.code && <span className="error-message">{errors.code}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('pages.admin.companyRegister.fields.ceo')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="ceo"
                  className={`form-input ${errors.ceo ? 'error' : ''}`}
                  value={formData.ceo}
                  onChange={handleChange}
                  placeholder={t('pages.admin.companyRegister.placeholder.ceo')}
                />
                {errors.ceo && <span className="error-message">{errors.ceo}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('pages.admin.companyRegister.fields.businessNumber')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="businessNumber"
                  className={`form-input ${errors.businessNumber ? 'error' : ''}`}
                  value={formData.businessNumber}
                  onChange={handleChange}
                  placeholder={t('pages.admin.companyRegister.placeholder.businessNumber')}
                />
                {errors.businessNumber && <span className="error-message">{errors.businessNumber}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">{t('pages.admin.companyRegister.sectionContact')}</h3>
            <div className="form-grid">
              <div className="form-group form-group-full">
                <label className="form-label">
                  {t('pages.admin.companyRegister.fields.address')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  className={`form-input ${errors.address ? 'error' : ''}`}
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={t('pages.admin.companyRegister.placeholder.address')}
                />
                {errors.address && <span className="error-message">{errors.address}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('pages.admin.companyRegister.fields.phone')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="phone"
                  className={`form-input ${errors.phone ? 'error' : ''}`}
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('pages.admin.companyRegister.placeholder.phone')}
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">{t('pages.admin.companyRegister.sectionOperation')}</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  {t('pages.admin.companyRegister.fields.employees')} <span className="required">*</span>
                </label>
                <input
                  type="number"
                  name="employees"
                  className={`form-input ${errors.employees ? 'error' : ''}`}
                  value={formData.employees}
                  onChange={handleChange}
                  placeholder={t('pages.admin.companyRegister.placeholder.employees')}
                  min="1"
                />
                {errors.employees && <span className="error-message">{errors.employees}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('pages.admin.companyRegister.fields.status')} <span className="required">*</span>
                </label>
                <div className="dropdown-container">
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="active">{t('pages.admin.companyRegister.statusActive')}</option>
                    <option value="inactive">{t('pages.admin.companyRegister.statusInactive')}</option>
                  </select>
                  <svg className="dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              {t('pages.admin.companyRegister.cancel')}
            </button>
            <button type="submit" className="btn-submit">
              {t('pages.admin.companyRegister.submit')}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
}

export default CompanyRegister;
